/**
 * SurveyResponseModal.tsx
 *
 * Read-only survey response viewer. Reproduces the legacy
 * `forms_survey_body.php` rendering as faithfully as possible so the
 * responses captured from the patient are displayed exactly as they
 * appeared in the legacy e-form viewer.
 *
 * Handled question types:
 *   1  Strongly Agree / Disagree scale
 *   2  Definitely Yes / Not scale
 *   3  Multi-line text
 *   4  Satisfied / Dissatisfied scale
 *   5  Star rating
 *   6  Textarea + public-posting checkbox
 *   7  Custom radio options (matrix layout when grouped + <6 options)
 *   8  Custom checkbox options (multi-select, comma-separated)
 *   9  Textarea
 *  10  Single-line text
 *  11  Signature (image)
 *  12  File upload (link)
 *  14  Single-line text
 *  16  Dropdown
 *  17  Height / weight / BMI (pipe-encoded)
 *  18  Single-line text
 *  19  Instruction only (no input)
 *  22  Text (pipe replaced with space)
 *  23  Instruction only (no input)
 *  24  Single-line text
 *  25  Textarea
 *  26  Single-line text
 *  28  Scroll & Accept (scrollable content with acceptance checkbox)
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Download, CheckCircle } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/apiClient';
import { getLegacyOrigin } from '@/lib/appStage';
import {
  getSurveyResponseDetail,
  getSurveyResponsePdf,
  type SurveyResponseDetail,
  type SurveyResponseItem,
} from '@/services/patientForms.service';
import { completedFormPdfFileName } from '@/services/completedFormPdf';
import { useCompletedFormPdf } from '@/hooks/useCompletedFormPdf';
import {
  markFormsInboxResponseRead,
  markFormsInboxResponseUnread,
  sendFormsInboxResponseToNotes,
  trashFormsInboxResponse,
  restoreFormsInboxResponse,
} from '@/services/formsInbox.service';

interface SurveyResponseModalProps {
  patientId: number;
  responseId: number | null;
  open: boolean;
  onClose: () => void;
  /**
   * When passed, the footer offers "Move to another patient" for a form that is
   * not deleted, and calls this to let the host open its move flow. Only
   * FormsTab passes it; NotificationModalHost and ConversationThread do not.
   */
  onMoveRequested?: () => void;
}

// Legacy PHP app origin (confirmed by user). All legacy survey assets
// (logo, uploaded files) are served from this host.
const LEGACY_ORIGIN = getLegacyOrigin();

// Prepend '#' to a color value if not already present; the legacy `surveys`
// table stores hex without the leading '#' and prepends it at render time.
function toCssColor(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

const BMI_INDEX_MAP: Record<string, string> = {
  '58': '4 feet 10 inches',
  '59': '4 feet 11 inches',
  '60': '5 feet 0 inches',
  '61': '5 feet 1 inches',
  '62': '5 feet 2 inches',
  '63': '5 feet 3 inches',
  '64': '5 feet 4 inches',
  '65': '5 feet 5 inches',
  '66': '5 feet 6 inches',
  '67': '5 feet 7 inches',
  '68': '5 feet 8 inches',
  '69': '5 feet 9 inches',
  '70': '5 feet 10 inches',
  '71': '5 feet 11 inches',
  '72': '6 feet 0 inches',
  '73': '6 feet 1 inches',
  '74': '6 feet 2 inches',
  '75': '6 feet 3 inches',
  '76': '6 feet 4 inches',
  '77': '6 feet 5 inches',
  '78': '6 feet 6 inches',
  '79': '6 feet 7 inches',
  '80': '6 feet 8 inches',
  '81': '6 feet 9 inches',
  '82': '6 feet 10 inches',
  '83': '6 feet 11 inches',
  '84': '7 feet 0 inches',
  '85': '7 feet 1 inches',
  '86': '7 feet 2 inches',
  '87': '7 feet 3 inches',
  '88': '7 feet 4 inches',
  '89': '7 feet 5 inches',
};

const SCALE_1_LABELS = ['Strongly Agree', 'Agree', 'No Opinion', 'Disagree', 'Strongly Disagree'];
const SCALE_2_LABELS = ['Definitely Yes', 'Probably Yes', 'Neutral', 'Probably Not', 'Definitely Not'];
const SCALE_4_LABELS = [
  'Completely Satisfied',
  'Satisfied',
  'Somewhat Satisfied',
  'Dissatisfied',
  'Completely Dissatisfied',
];

// Legacy stores composite values like "First|Last" using '|' as a separator
// (legacy only unpipes for questionType 22, but the same convention shows up
// in textarea/text types when a form was built from a "Full Name" widget).
// We replace '|' with a space for all free-text types EXCEPT type 17, which
// uses '|' semantically as the height|weight separator for BMI.
const PIPE_AS_SPACE_TYPES = new Set<number>([3, 9, 10, 14, 18, 22, 24, 25, 26]);

function displayTextValue(item: SurveyResponseItem): string {
  const raw = item.value ?? '';
  if (PIPE_AS_SPACE_TYPES.has(item.questionType)) {
    return raw.replace(/\|/g, ' ');
  }
  return raw;
}

// Render legacy question / group / instruction strings as HTML. Legacy PHP
// echoes these fields raw (`<?php echo $questionContent; ?>`), so stored
// content often includes inline <span>, <strong>, and basic styling.
function htmlProps(html: string | null | undefined) {
  return { dangerouslySetInnerHTML: { __html: html ?? '' } };
}

function parseSelectedIds(value: string | null): Set<number> {
  if (!value) return new Set();
  return new Set(
    value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n)),
  );
}

function computeBmi(value: string | null): { heightText: string; weight: string; bmi: string } {
  if (!value) return { heightText: '', weight: '', bmi: '' };
  const [hRaw, wRaw] = value.split('|');
  const heightText = hRaw && BMI_INDEX_MAP[hRaw.trim()] ? BMI_INDEX_MAP[hRaw.trim()] : '';
  const weightNum = Number(wRaw);
  const heightNum = Number(hRaw);
  let bmi = '';
  if (Number.isFinite(weightNum) && Number.isFinite(heightNum) && weightNum > 0 && heightNum > 0) {
    const wKg = weightNum / 2.20462262;
    const hM = heightNum / 39.3701;
    bmi = (wKg / (hM * hM)).toFixed(1);
  }
  return {
    heightText,
    weight: Number.isFinite(weightNum) ? String(weightNum) : '',
    bmi,
  };
}

// ─── Styling helpers ──────────────────────────────────────────────────────────

const contentTextStyle: React.CSSProperties = {
  fontFamily: "'Roboto', sans-serif",
  fontSize: '1.1em',
};

function bannerStyle(color: string, topRounded = false, bottomRounded = false): React.CSSProperties {
  return {
    padding: 8,
    position: 'relative',
    width: '100%',
    background: color,
    border: '1px solid #b0b0b0',
    boxSizing: 'border-box',
    ...(topRounded ? { borderTopLeftRadius: 10, borderTopRightRadius: 10, marginTop: 10 } : null),
    ...(bottomRounded
      ? { borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }
      : null),
  };
}

// ─── Question renderers ───────────────────────────────────────────────────────

interface RenderContext {
  color1: string;
  color2: string;
  itemCount: number;
  previousQuestionType: number;
  nextQuestionType: number;
  previousGroupId: number;
  headerRenewCount: number;
  onFilePreview?: (url: string, name: string) => void;
}

function renderScaleQuestion(
  item: SurveyResponseItem,
  labels: string[],
  ctx: RenderContext,
): React.ReactNode {
  const showTopRound = ctx.previousQuestionType !== item.questionType;
  const showBottomRound = ctx.nextQuestionType !== item.questionType;
  return (
    <div key={`q-${item.orderId}`}>
      <div style={bannerStyle(ctx.color1, showTopRound, false)}>
        <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '1.0em' }}>
          <strong style={{ fontSize: '1.0em' }}>
            {ctx.itemCount}. <span {...htmlProps(item.questionContent)} />
          </strong>
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={bannerStyle(ctx.color2, false, showBottomRound)}>
        <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '0.9em' }}>
          {[5, 4, 3, 2, 1].map((val, idx) => (
            <div key={val} style={{ padding: 3 }}>
              <input
                type="radio"
                readOnly
                disabled
                checked={String(item.value) === String(val)}
                style={{ marginLeft: 10 }}
              />
              <label style={{ marginLeft: 5 }}>{labels[idx]}</label>
            </div>
          ))}
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={{ clear: 'both' }} />
    </div>
  );
}

function renderStar(item: SurveyResponseItem, ctx: RenderContext): React.ReactNode {
  const showTopRound = ctx.previousQuestionType !== item.questionType;
  const showBottomRound = ctx.nextQuestionType !== item.questionType;
  const starNum = Math.max(0, Math.min(5, Number(item.value ?? '0') || 0));
  return (
    <div key={`q-${item.orderId}`}>
      <div style={bannerStyle(ctx.color1, showTopRound, false)}>
        <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '1.0em' }}>
          <strong style={{ fontSize: '1.0em' }}>
            {ctx.itemCount}. <span {...htmlProps(item.questionContent)} />
          </strong>
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={bannerStyle(ctx.color2, false, showBottomRound)}>
        <div style={{ padding: 3 }}>
          {[1, 2, 3, 4, 5].map((n) => (
            <svg
              key={n}
              viewBox="0 0 24 24"
              width={32}
              height={30}
              style={{ marginBottom: -5, marginRight: 1 }}
              aria-label={n <= starNum ? 'filled star' : 'empty star'}
            >
              <path
                d="M12 2l2.9 6.9 7.1.6-5.4 4.7 1.7 7L12 17.8 5.7 21.2l1.7-7L2 9.5l7.1-.6z"
                fill={n <= starNum ? '#f5b301' : '#dddddd'}
                stroke="#b0b0b0"
                strokeWidth="0.5"
              />
            </svg>
          ))}
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={{ clear: 'both' }} />
    </div>
  );
}

function renderTextareaSimple(item: SurveyResponseItem, ctx: RenderContext, numbered: boolean): React.ReactNode {
  const showTopRound = ctx.previousQuestionType !== item.questionType;
  const showBottomRound = ctx.nextQuestionType !== item.questionType;
  return (
    <div key={`q-${item.orderId}`}>
      <div style={bannerStyle(ctx.color1, showTopRound, false)}>
        <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '1.0em' }}>
          <strong style={{ fontSize: '1.0em' }}>
            {numbered ? `${ctx.itemCount}. ` : ''}
            <span {...htmlProps(item.questionContent)} />
          </strong>
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={bannerStyle(ctx.color2, false, showBottomRound)}>
        <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
          <textarea
            rows={5}
            readOnly
            disabled
            value={displayTextValue(item)}
            style={{ width: '98%', fontSize: '1.1em' }}
          />
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={{ clear: 'both' }} />
    </div>
  );
}

function renderTextareaWithApproval(item: SurveyResponseItem, ctx: RenderContext): React.ReactNode {
  const showTopRound = ctx.previousQuestionType !== item.questionType;
  const showBottomRound = ctx.nextQuestionType !== item.questionType;
  return (
    <div key={`q-${item.orderId}`}>
      <div style={bannerStyle(ctx.color1, showTopRound, false)}>
        <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '1.0em' }}>
          <strong style={{ fontSize: '1.0em' }}>
            {ctx.itemCount}. <span {...htmlProps(item.questionContent)} />
          </strong>
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={bannerStyle(ctx.color2, false, showBottomRound)}>
        <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
          <textarea
            rows={5}
            readOnly
            disabled
            value={displayTextValue(item)}
            style={{ marginBottom: 6, width: '98%', fontSize: '1.1em' }}
          />
          <div>
            <input type="checkbox" disabled readOnly checked={!!item.approved} /> This review is approved for public posting by the
            practice.
          </div>
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={{ clear: 'both' }} />
    </div>
  );
}

function renderSimpleBox(
  item: SurveyResponseItem,
  ctx: RenderContext,
  inner: React.ReactNode,
  showGroupHeader: boolean,
  groupTitle: string,
): React.ReactNode {
  return (
    <div key={`q-${item.orderId}`}>
      {showGroupHeader && groupTitle !== '' && (
        <div
          style={{
            ...bannerStyle(ctx.color1, true, false),
            marginTop: 10,
          }}
        >
          <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '1.0em' }}>
            <strong style={{ fontSize: '1.0em' }} {...htmlProps(groupTitle)} />
          </div>
          <div style={{ clear: 'both' }} />
        </div>
      )}
      {!showGroupHeader && <div style={{ marginTop: 10 }} />}
      <div style={bannerStyle(ctx.color2)}>
        {inner}
        <div style={{ clear: 'both' }} />
      </div>
      <div style={{ clear: 'both' }} />
    </div>
  );
}

function renderType7List(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  const selected = parseSelectedIds(item.value);
  const otherText = parseOtherText(item.value);
  const inner = (
    <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '0.9em', width: '100%' }}>
      <div
        style={{ fontWeight: 'bold', marginLeft: 0, marginBottom: 5 }}
        {...htmlProps(item.questionContent)}
      />
      {item.options.map((opt) => (
        <div key={opt.id} style={{ padding: 3 }}>
          <input
            type="radio"
            readOnly
            disabled
            checked={selected.has(opt.id)}
            style={{ marginLeft: 10 }}
          />
          <label style={{ marginLeft: 5 }}>{opt.text}</label>
        </div>
      ))}
      {otherText !== null && (
        <div style={{ padding: 3 }}>
          <input type="radio" readOnly disabled checked style={{ marginLeft: 10 }} />
          <label style={{ marginLeft: 5 }}>Other: {otherText}</label>
        </div>
      )}
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderType7Matrix(
  item: SurveyResponseItem,
  ctx: RenderContext,
  showHeaderRow: boolean,
  newGroup: boolean,
): React.ReactNode {
  const selected = parseSelectedIds(item.value);
  const cellCount = item.options.length || 1;
  const cellWidth = 70 / cellCount;
  return (
    <div key={`q-${item.orderId}`}>
      {newGroup && item.groupTitle !== '' && (
        <div
          style={{
            ...bannerStyle(ctx.color1, true, false),
            marginTop: 10,
          }}
        >
          <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '1.0em' }}>
            <strong style={{ fontSize: '1.0em' }} {...htmlProps(item.groupTitle)} />
          </div>
          <div style={{ clear: 'both' }} />
        </div>
      )}
      {!newGroup && <div style={{ marginTop: 10 }} />}
      {showHeaderRow && (
        <div style={bannerStyle(ctx.color2)}>
          <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '0.9em', width: '100%' }}>
            <div style={{ width: '100%' }}>
              <div style={{ float: 'left', width: '30%' }}>&nbsp;</div>
              {item.options.map((opt) => (
                <div
                  key={opt.id}
                  style={{
                    float: 'left',
                    width: `${cellWidth}%`,
                    textAlign: 'center',
                    fontSize: '0.9em',
                  }}
                >
                  {opt.text}
                </div>
              ))}
              <div style={{ clear: 'both' }} />
            </div>
          </div>
          <div style={{ clear: 'both' }} />
        </div>
      )}
      <div style={bannerStyle(ctx.color2)}>
        <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '0.9em', width: '100%' }}>
          <div
            style={{ float: 'left', width: '30%', fontWeight: 'bold' }}
            {...htmlProps(item.questionContent)}
          />
          {item.options.map((opt) => (
            <div
              key={opt.id}
              style={{ float: 'left', width: `${cellWidth}%`, textAlign: 'center' }}
            >
              <input type="radio" readOnly disabled checked={selected.has(opt.id)} />
            </div>
          ))}
        </div>
        <div style={{ clear: 'both' }} />
      </div>
      <div style={{ clear: 'both' }} />
    </div>
  );
}

/**
 * A choice answer's "Other" text: every part that is not an option ID. FTP's
 * page stores it after the IDs with no prefix ("12,15,Chess"), as Prospera
 * does since R40; older Prospera rows carry an "other:" prefix. The text's own
 * commas split it, so the parts are joined back ("12,knees, elbows" is
 * "knees, elbows"). A bare number is always an ID, even one no longer in the
 * list, as the note builder (SurveyNoteHtmlBuilder) reads it.
 */
export function parseOtherText(value: string | null): string | null {
  if (!value) return null;
  const parts: string[] = [];
  for (const part of value.split(',')) {
    const trimmed = part.trim();
    if (trimmed.startsWith('other:')) {
      parts.push(trimmed.substring(6));
    } else if (trimmed !== '' && !/^\d+$/.test(trimmed)) {
      parts.push(trimmed);
    }
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

function renderType8(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  const selected = parseSelectedIds(item.value);
  const otherText = parseOtherText(item.value);
  const inner = (
    <div style={{ ...contentTextStyle, position: 'relative', float: 'left', fontSize: '0.9em' }}>
      <div style={{ fontWeight: 'bold', marginLeft: 10 }} {...htmlProps(item.questionContent)} />
      {item.options.map((opt) => (
        <div key={opt.id} style={{ padding: 3 }}>
          <input type="checkbox" readOnly disabled checked={selected.has(opt.id)} style={{ marginLeft: 10 }} />
          <label style={{ marginLeft: 5 }}>{opt.text}</label>
        </div>
      ))}
      {otherText !== null && (
        <div style={{ padding: 3 }}>
          <input type="checkbox" readOnly disabled checked style={{ marginLeft: 10 }} />
          <label style={{ marginLeft: 5 }}>Other: {otherText}</label>
        </div>
      )}
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderType16(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  const selectedId = Number(item.value ?? 0);
  const inner = (
    <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
      <div
        style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
        {...htmlProps(item.questionContent)}
      />
      <select disabled value={Number.isFinite(selectedId) ? String(selectedId) : ''} style={{ width: '98%' }}>
        <option value="">Select one...</option>
        {item.options.map((opt) => (
          <option key={opt.id} value={String(opt.id)}>
            {opt.text}
          </option>
        ))}
      </select>
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderSingleLineText(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  const inner = (
    <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
      <div
        style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
        {...htmlProps(item.questionContent)}
      />
      {item.questionType !== 19 && item.questionType !== 23 ? (
        <input
          type="text"
          readOnly
          disabled
          value={displayTextValue(item)}
          style={{ width: '98%', fontSize: '1.1em' }}
        />
      ) : (
        <div
          style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
          {...htmlProps(item.instructionText)}
        />
      )}
    </div>
  );
  // Type 17 adds weight and BMI rows.
  if (item.questionType === 17 && item.value) {
    const { heightText, weight, bmi } = computeBmi(item.value);
    return renderSimpleBox(
      item,
      ctx,
      <>
        <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
          <div
            style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
            {...htmlProps(item.questionContent)}
          />
          <input
            type="text"
            readOnly
            disabled
            value={heightText}
            style={{ width: '98%', fontSize: '1.1em' }}
          />
        </div>
        {weight !== '' && (
          <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
            <div style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}>Weight in lbs.</div>
            <input
              type="text"
              readOnly
              disabled
              value={weight}
              style={{ width: '98%', fontSize: '1.1em' }}
            />
          </div>
        )}
        {bmi !== '' && (
          <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
            <div style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}>BMI</div>
            <input
              type="text"
              readOnly
              disabled
              value={bmi}
              style={{ width: '98%', fontSize: '1.1em' }}
            />
          </div>
        )}
      </>,
      newGroup,
      item.groupTitle,
    );
  }
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderScrollAccept(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  // Match legacy: strtolower(trim(...)) === 'accepted' — a stored value with
  // surrounding whitespace/newline must still read as accepted.
  const accepted = (item.value ?? '').trim().toLowerCase() === 'accepted';
  // No stored answer: left unticked, added after the patient submitted, or lost by
  // an older form — so it is not shown as a refusal.
  const unanswered = item.value === null;
  const inner = (
    <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
      <div
        style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
        {...htmlProps(item.questionContent)}
      />
      {item.instructionText && (
        <div
          style={{ marginLeft: 0, width: '99%', marginTop: 4 }}
          {...htmlProps(item.instructionText)}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '6px 0' }}>
        <input
          type="checkbox"
          readOnly
          disabled
          checked={accepted}
          style={{ width: 16, height: 16 }}
        />
        <span
          style={{
            fontSize: '1.1em',
            color: accepted ? '#16a34a' : unanswered ? '#6b7280' : '#dc2626',
            fontWeight: 600,
          }}
        >
          {accepted ? 'Accepted' : unanswered ? 'No answer recorded' : 'Not accepted'}
        </span>
      </div>
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderSignature(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  const src = (item.value ?? '').replace(/ /g, '+');
  const inner = (
    <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
      <div
        style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
        {...htmlProps(item.questionContent)}
      />
      <div
        style={{
          position: 'relative',
          width: '97%',
          minHeight: 150,
          maxWidth: 800,
          background: '#ffffff',
          borderRadius: 5,
          border: '2px solid #bbb',
          marginBottom: 10,
        }}
      >
        {src !== '' ? (
          <img src={src} alt="Signature" style={{ width: 700, maxWidth: '100%' }} />
        ) : (
          <div style={{ padding: 20, color: '#888' }}>(no signature captured)</div>
        )}
      </div>
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderFile(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean, responseId: number): React.ReactNode {
  const fileName = item.value ?? '';
  const apiUrl = fileName !== ''
    ? `/eforms/${responseId}/uploads/download?name=${encodeURIComponent(fileName)}&inline=true`
    : '';
  const inner = (
    <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
      <div
        style={{ fontWeight: 'bold', marginLeft: 0, width: '99%' }}
        {...htmlProps(item.questionContent)}
      />
      {fileName !== '' ? (
        <button
          type="button"
          onClick={() => ctx.onFilePreview?.(apiUrl, fileName)}
          style={{ color: '#4338ca', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0, font: 'inherit' }}
        >
          {fileName}
        </button>
      ) : (
        <span style={{ color: '#888' }}>(no file uploaded)</span>
      )}
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

function renderOptInToggles(item: SurveyResponseItem, ctx: RenderContext, newGroup: boolean): React.ReactNode {
  // The patient form always submits this question (it starts at 1|1|1|1), so no
  // stored answer means the patient never had it. Showing a default would read as
  // opt-ins the patient never gave.
  if (!item.value) {
    return renderSimpleBox(
      item,
      ctx,
      <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
        <div style={{ fontWeight: 'bold', marginBottom: 4 }} {...htmlProps(item.questionContent)} />
        <span style={{ color: '#888' }}>(not answered)</span>
      </div>,
      newGroup,
      item.groupTitle,
    );
  }
  const parts = item.value.split('|');
  const emailOptIn = parts[0] === '1';
  const smsOptIn = parts[1] === '1';
  const marketingEmail = parts[2] === '1';
  const marketingSms = parts[3] === '1';

  const toggleDot = (on: boolean) => (
    <span
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        backgroundColor: on ? '#16a34a' : '#d1d5db',
        marginRight: 4,
        verticalAlign: 'middle',
      }}
    />
  );

  const inner = (
    <div style={{ width: '100%', padding: 8, fontSize: '0.8em' }}>
      <div style={{ fontWeight: 'bold', marginBottom: 4 }} {...htmlProps(item.questionContent)} />
      <div style={{ marginBottom: 6 }}>
        <strong style={{ fontSize: '0.95em' }}>Transactional</strong>
        <span style={{ color: '#64748b', fontSize: '0.85em' }}> – Confirmations, receipts, notifications</span>
        <div style={{ marginTop: 2 }}>
          {toggleDot(emailOptIn)} Email: {emailOptIn ? 'On' : 'Off'}
          <span style={{ marginLeft: 16 }}>{toggleDot(smsOptIn)} SMS: {smsOptIn ? 'On' : 'Off'}</span>
        </div>
      </div>
      <div>
        <strong style={{ fontSize: '0.95em' }}>Marketing</strong>
        <span style={{ color: '#64748b', fontSize: '0.85em' }}> – Promotions, newsletters, campaigns</span>
        <div style={{ marginTop: 2 }}>
          {toggleDot(marketingEmail)} Email: {marketingEmail ? 'On' : 'Off'}
          <span style={{ marginLeft: 16 }}>{toggleDot(marketingSms)} SMS: {marketingSms ? 'On' : 'Off'}</span>
        </div>
      </div>
    </div>
  );
  return renderSimpleBox(item, ctx, inner, newGroup, item.groupTitle);
}

// A choice question (7, 8) has a shared option list (customQuestionType), or,
// failing that, its own per-question list, which the API returns in `options`.
function hasChoiceList(item: SurveyResponseItem): boolean {
  return item.customQuestionType !== 0 || item.options.length > 0;
}

// ─── Main item dispatcher ─────────────────────────────────────────────────────

function renderItem(
  item: SurveyResponseItem,
  ctx: RenderContext,
  isNewGroup: boolean,
  type7MatrixHeader: boolean,
  responseId: number,
): React.ReactNode {
  const qt = item.questionType;
  switch (qt) {
    case 1:
      return renderScaleQuestion(item, SCALE_1_LABELS, ctx);
    case 2:
      return renderScaleQuestion(item, SCALE_2_LABELS, ctx);
    case 3:
      return renderTextareaSimple(item, ctx, true);
    case 4:
      return renderScaleQuestion(item, SCALE_4_LABELS, ctx);
    case 5:
      return renderStar(item, ctx);
    case 6:
      return renderTextareaWithApproval(item, ctx);
    case 7: {
      if (!hasChoiceList(item)) return null;
      const isMatrix = item.groupId !== 0 && item.options.length < 6;
      return isMatrix
        ? renderType7Matrix(item, ctx, type7MatrixHeader, isNewGroup)
        : renderType7List(item, ctx, isNewGroup);
    }
    case 8:
      if (!hasChoiceList(item)) return null;
      return renderType8(item, ctx, isNewGroup);
    case 9:
    case 25:
      return renderTextareaSimple(item, ctx, false);
    case 11:
      return renderSignature(item, ctx, isNewGroup);
    case 12:
      return renderFile(item, ctx, isNewGroup, responseId);
    case 16:
      return renderType16(item, ctx, isNewGroup);
    case 10:
    case 14:
    case 15:
    case 17:
    case 18:
    case 19:
    case 22:
    case 23:
    case 24:
    case 26:
      return renderSingleLineText(item, ctx, isNewGroup);
    case 27:
      return renderOptInToggles(item, ctx, isNewGroup);
    case 28:
      return renderScrollAccept(item, ctx, isNewGroup);
    default:
      return null;
  }
}

// ─── Body ─────────────────────────────────────────────────────────────────────

export function SurveyResponseBody({
  detail,
  responseId,
}: {
  detail: SurveyResponseDetail;
  responseId: number;
}) {
  const [previewFile, setPreviewFile] = useState<{ url: string; name: string } | null>(null);

  // Debug: log each field's type + stored value when the form opens, so missing
  // signatures (type 11) and scroll-accept (type 28) not registering can be
  // diagnosed straight from the browser console.
  useEffect(() => {
    const items = detail.items.map((it) => ({
      orderId: it.orderId,
      itemId: it.itemId,
      questionType: it.questionType,
      active: it.active,
      valueLen: (it.value ?? '').length,
      valuePreview: (it.value ?? '').slice(0, 80),
    }));
    // eslint-disable-next-line no-console
    console.log('[FormModal] response', responseId, 'survey', detail.survey.id, {
      signature_type11: items.filter((i) => i.questionType === 11),
      scrollAccept_type28: items.filter((i) => i.questionType === 28),
      allItems: items,
    });
  }, [detail, responseId]);

  const color1 = toCssColor(detail.survey.color1);
  const color2 = toCssColor(detail.survey.color2);
  const accountId = detail.survey.accountId;
  const logoUrl = `${LEGACY_ORIGIN}/uimages/img${accountId}/logo.jpg`;

  // Mirror the legacy iteration state machine.
  let previousQuestionType = 0;
  let previousGroupId = 0;
  let headerRenewCount = 0;
  // itemCount is incremented by the legacy code for these scale-like types.
  // Other types ignore it (they don't prefix with a number).
  let itemCount = 0;

  const nodes: React.ReactNode[] = [];
  const items = detail.items;

  // An inactive item is shown only when the patient answered it: a question
  // deactivated after the form was filled in is still part of what was filled in.
  const shown = (item: SurveyResponseItem) => item.active || item.value !== null;

  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    if (!shown(it)) {
      continue;
    }

    const nextActive = (() => {
      for (let j = i + 1; j < items.length; j++) {
        if (shown(items[j])) return items[j];
      }
      return null;
    })();

    // Legacy quirk: treat 3<->6 adjacency as the same type for rounding purposes.
    let effPrev = previousQuestionType;
    let effNext = nextActive ? nextActive.questionType : 0;
    if ((effPrev === 6 && it.questionType === 3) || (effPrev === 3 && it.questionType === 6)) {
      effPrev = it.questionType;
    }
    if ((effNext === 6 && it.questionType === 3) || (effNext === 3 && it.questionType === 6)) {
      effNext = it.questionType;
    }

    const isNewGroup = it.groupId !== previousGroupId;

    // Type 7 matrix "header row" rule: show when entering a new group,
    // and repeat every 5 rows within the group.
    let type7MatrixHeader = false;
    if (it.questionType === 7 && hasChoiceList(it)) {
      const isMatrix = it.groupId !== 0 && it.options.length < 6;
      if (isMatrix) {
        if (isNewGroup) {
          headerRenewCount = 1;
          type7MatrixHeader = true;
        } else {
          headerRenewCount += 1;
          if (headerRenewCount > 4) {
            headerRenewCount = 1;
            type7MatrixHeader = true;
          }
        }
      } else {
        headerRenewCount = 0;
      }
    } else {
      headerRenewCount = 0;
    }

    // Increment itemCount for numbered types so the numbering matches legacy exactly.
    // In the legacy code, $itemCount is incremented inside every type block that renders.
    itemCount += 1;

    const ctx: RenderContext = {
      color1,
      color2,
      itemCount,
      previousQuestionType: effPrev,
      nextQuestionType: effNext,
      previousGroupId,
      headerRenewCount,
      onFilePreview: (url, name) => setPreviewFile({ url, name }),
    };

    nodes.push(renderItem(it, ctx, isNewGroup, type7MatrixHeader, responseId));

    previousQuestionType = it.questionType;
    previousGroupId = it.groupId;
  }

  return (
    <>
    {detail.response.versioned === false && (
      <p className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
        This copy uses the form&apos;s current wording, which may differ from what was shown when it was submitted.
      </p>
    )}
    <div
      style={{
        ...contentTextStyle,
        width: '100%',
        position: 'relative',
        marginTop: 15,
        background: `linear-gradient(to bottom, ${color1} 0%, rgba(255,255,255,1) 60%)`,
        paddingBottom: 20,
      }}
    >
      <div
        style={{
          marginLeft: 'auto',
          marginRight: 'auto',
          minWidth: 650,
          maxWidth: 720,
          background: '#FFFFFF',
          border: '1px solid #b0b0b0',
          borderRadius: 10,
        }}
      >
        <div style={{ padding: 12 }}>
          {/*
            Legacy renders the survey title (which often already embeds the
            "(updated <date>)" suffix) inside this card, but the modal title
            bar already shows the full string. We keep the account logo and
            drop the inline <h1> to avoid the redundant repetition.
          */}
          <div style={{ position: 'relative', minWidth: 600, width: '100%', maxWidth: 676 }}>
            <div
              style={{
                marginBottom: 10,
                position: 'relative',
                float: 'right',
                textAlign: 'right',
                marginRight: 0,
                width: 170,
                fontWeight: 'bold',
                color: '#22507e',
              }}
            >
              <img
                src={logoUrl}
                alt=""
                width={170}
                style={{ float: 'right' }}
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            </div>
            <div style={{ clear: 'both' }} />
          </div>
          <div style={{ position: 'relative', minWidth: 500, width: '100%', maxWidth: 660 }}>
            {detail.survey.headerMessage && (
              <div style={{ marginLeft: 5, marginBottom: 5 }}>
                <div
                  style={{ fontSize: '0.8em', marginTop: 5 }}
                  // Legacy survey headers can contain trusted HTML.
                  dangerouslySetInnerHTML={{ __html: detail.survey.headerMessage }}
                />
              </div>
            )}
            {nodes}
            <div style={{ clear: 'both' }} />
            {(detail.physician.name !== '' ||
              detail.facility.name !== '' ||
              detail.facility.address !== '' ||
              detail.facility.phone !== '') && (
              <div
                style={{
                  fontSize: '1.1em',
                  textAlign: 'center',
                  padding: 10,
                  fontFamily: "'Roboto', sans-serif",
                  marginTop: 15,
                  width: '99%',
                  background: color1,
                  color: '#FFFFFF',
                  border: '1px solid #b0b0b0',
                  borderRadius: 10,
                }}
              >
                {detail.physician.name !== '' && (
                  <div style={{ fontWeight: 'bold' }}>{detail.physician.name}</div>
                )}
                {detail.facility.name !== '' && <div>{detail.facility.name}</div>}
                {detail.facility.address !== '' && <div>{detail.facility.address}</div>}
                {detail.facility.phone !== '' && <div>{detail.facility.phone}</div>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    {previewFile && (
      <EformFilePreviewModal
        url={previewFile.url}
        name={previewFile.name}
        onClose={() => setPreviewFile(null)}
      />
    )}
    </>
  );
}

function EformFilePreviewModal({ url, name, onClose }: { url: string; name: string; onClose: () => void }) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff', 'tif'].includes(ext);
  const isPdf = ext === 'pdf';
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => { document.body.style.overflow = ''; document.removeEventListener('keydown', handler); };
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get(url, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        setBlobUrl(URL.createObjectURL(res.data as Blob));
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] flex flex-col rounded-lg bg-white shadow-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 flex-shrink-0">
          <h3 className="text-sm font-semibold text-slate-900 truncate">{name}</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!blobUrl}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" />
              Download
            </button>
            <button type="button" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-slate-100 flex items-center justify-center p-4 min-h-[300px]">
          {loading ? (
            <Spinner className="h-8 w-8 text-slate-400" />
          ) : blobUrl && isImage ? (
            <img src={blobUrl} alt={name} className="max-w-full max-h-[75vh] object-contain rounded shadow-lg" />
          ) : blobUrl && isPdf ? (
            <iframe src={blobUrl} title={name} className="w-full h-[75vh] rounded border border-slate-200" />
          ) : (
            <div className="text-center space-y-3">
              <p className="text-sm text-slate-500">Preview not available for this file type.</p>
              <button
                type="button"
                onClick={handleDownload}
                disabled={!blobUrl}
                className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Download className="h-4 w-4" />
                Download File
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────

type ToastKind = 'success' | 'error';
interface InlineToast { kind: ToastKind; message: string; }

interface FormMovedTo { patientId: number; patientName: string; }

/**
 * The API answers 409 FORM_MOVED when the form now belongs to another chart,
 * e.g. it was moved after this chart's comm-log card or alert was written.
 */
function formMovedTo(error: unknown): FormMovedTo | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return null;
  const body = error.response.data as
    | { error?: { code?: string; details?: { patientId?: unknown; patientName?: unknown } } }
    | undefined;
  if (body?.error?.code !== 'FORM_MOVED') return null;
  const patientId = Number(body.error.details?.patientId);
  if (!Number.isInteger(patientId) || patientId <= 0) return null;
  const patientName = typeof body.error.details?.patientName === 'string' ? body.error.details.patientName.trim() : '';
  return { patientId, patientName };
}

function FormMovedNotice({ movedTo, onOpenChart }: { movedTo: FormMovedTo; onOpenChart: () => void }) {
  const whose = movedTo.patientName !== '' ? `${movedTo.patientName}'s` : "another patient's";
  return (
    <div className="space-y-3 py-6 text-center text-sm text-slate-700">
      <p>{`This form was moved to ${whose} chart.`}</p>
      <Link
        to={`/patients/${movedTo.patientId}`}
        onClick={onOpenChart}
        className="font-medium text-blue-700 underline-offset-2 hover:underline"
      >
        {movedTo.patientName !== '' ? `Open ${movedTo.patientName}'s chart` : 'Open that chart'}
      </Link>
    </div>
  );
}

export function SurveyResponseModal({ patientId, responseId, open, onClose, onMoveRequested }: SurveyResponseModalProps) {
  const queryClient = useQueryClient();
  const { hasMinUserLevel } = useAuth();
  // While trialing on production, only level-5 users trigger the on-view recovery
  // of missing legacy answers (signature / scroll-accept) from the lead HTML.
  const canRecover = hasMinUserLevel(5);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [toast, setToast] = useState<InlineToast | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['patient-form-survey-response', patientId, responseId, canRecover],
    queryFn: () => getSurveyResponseDetail(patientId, responseId!, canRecover),
    enabled: open && responseId !== null,
  });

  useEffect(() => {
    if (open) {
      setConfirmingDelete(false);
      setToast(null);
    }
  }, [open, responseId]);

  const invalidate = () => {
    // FormsTab lists under ['patient-forms-list', patientId, showDeleted]; invalidate by the
    // ['patient-forms-list', patientId] prefix so both the deleted/active views refresh.
    queryClient.invalidateQueries({ queryKey: ['patient-forms-list', patientId] });
    queryClient.invalidateQueries({ queryKey: ['patient-forms', patientId] });
    queryClient.invalidateQueries({ queryKey: ['forms-inbox'] });
    queryClient.invalidateQueries({ queryKey: ['forms-inbox-unread-count'] });
  };

  const isUnread = !(data?.response.wasRead ?? false);

  const toggleReadMutation = useMutation({
    mutationFn: () =>
      isUnread
        ? markFormsInboxResponseRead(responseId!)
        : markFormsInboxResponseUnread(responseId!),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (err) => {
      setToast({ kind: 'error', message: err instanceof Error ? err.message : `Failed to mark form as ${isUnread ? 'read' : 'unread'}.` });
    },
  });

  const trashMutation = useMutation({
    mutationFn: () => trashFormsInboxResponse(responseId!),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (err) => {
      setToast({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to delete form.' });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => restoreFormsInboxResponse(responseId!),
    onSuccess: () => { invalidate(); onClose(); },
    onError: (err) => {
      setToast({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to restore form.' });
    },
  });

  const sendToNotesMutation = useMutation({
    mutationFn: () => sendFormsInboxResponseToNotes(responseId!),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['patient-notes'] });
      setToast({ kind: 'success', message: result.message ?? 'Survey content successfully saved as patient note.' });
    },
    onError: (err) => {
      setToast({ kind: 'error', message: err instanceof Error ? err.message : 'Failed to send to notes.' });
    },
  });

  const pdf = useCompletedFormPdf({
    fetchPdf: () => getSurveyResponsePdf(patientId, responseId!),
    setToast,
  });
  // Only a submitted form has a PDF; the server refuses one that is not.
  const canGetPdf = (data?.response.dateCompleted ?? '') !== '';

  const handleDownloadPdf = () => {
    if (!data) return;
    pdf.download(completedFormPdfFileName(data.survey.title, data.response.dateCompleted ?? data.response.dateCreated));
  };

  const movedTo = formMovedTo(error);
  const isDeleted = data?.response.endDate != null;
  const anyPending = toggleReadMutation.isPending || trashMutation.isPending || sendToNotesMutation.isPending || restoreMutation.isPending;

  const handleClose = () => {
    if (anyPending) return;
    onClose();
  };

  // A refetch that finds the form moved keeps the earlier data; offer no
  // actions on a form this chart no longer has.
  const footerContent = data && !movedTo ? (
    <div className="flex flex-wrap items-center gap-2 w-full justify-end">
      {toast && (
        <span
          className={`mr-auto text-xs ${
            toast.kind === 'success' ? 'text-emerald-700' : 'text-red-600'
          }`}
        >
          {toast.message}
        </span>
      )}
      {confirmingDelete ? (
        <>
          <span className="text-sm text-slate-700">
            Permanently remove this submission?
          </span>
          <Button
            variant="secondary"
            onClick={() => setConfirmingDelete(false)}
            disabled={trashMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => trashMutation.mutate()}
            disabled={trashMutation.isPending}
          >
            {trashMutation.isPending ? 'Deleting...' : 'Confirm Delete'}
          </Button>
        </>
      ) : (
        <>
          {canGetPdf && (
            <>
              <Button variant="secondary" onClick={handleDownloadPdf} disabled={pdf.pending}>
                {pdf.downloadPending ? 'Preparing…' : 'Download PDF'}
              </Button>
              <Button variant="secondary" onClick={pdf.print} disabled={pdf.pending}>
                {pdf.printPending ? 'Preparing…' : 'Print'}
              </Button>
            </>
          )}
          {patientId > 0 && (
            <Button
              variant="secondary"
              onClick={() => sendToNotesMutation.mutate()}
              disabled={sendToNotesMutation.isPending}
            >
              {sendToNotesMutation.isPending ? 'Sending...' : 'Send to Notes'}
            </Button>
          )}
          {onMoveRequested && !isDeleted && (
            <Button variant="secondary" onClick={onMoveRequested} disabled={anyPending}>
              Move to another patient
            </Button>
          )}
          {isDeleted ? (
            <Button
              variant="secondary"
              onClick={() => restoreMutation.mutate()}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? 'Restoring...' : 'Restore'}
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={() => setConfirmingDelete(true)}
            >
              Delete
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => toggleReadMutation.mutate()}
            disabled={toggleReadMutation.isPending}
          >
            {toggleReadMutation.isPending
              ? (isUnread ? 'Marking Read...' : 'Marking Unread...')
              : (isUnread ? 'Mark as Read' : 'Mark as Unread')}
          </Button>
        </>
      )}
    </div>
  ) : undefined;

  return (
    <Modal isOpen={open} onClose={handleClose} title={
      <h2 className="inline-flex items-center gap-2 text-base font-semibold text-slate-900">
        {data?.survey.title ?? 'Form'}
        {data?.response.wasRead === true && <span title="Read"><CheckCircle className="h-4 w-4 text-emerald-500" /></span>}
      </h2>
    } size="xxl" footer={footerContent}>
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : movedTo ? (
        <FormMovedNotice movedTo={movedTo} onOpenChart={onClose} />
      ) : isError || !data ? (
        <p className="py-6 text-center text-sm text-red-600">Failed to load response.</p>
      ) : (
        <SurveyResponseBody detail={data} responseId={responseId ?? 0} />
      )}
    </Modal>
  );
}
